import React from 'react';
import { useAppState } from '@frontend/howl-web-app/overmind';
import { Column, Table } from '../ui/table';
import { ExternalLink } from 'react-feather';
import { useRouter } from 'next/router';
import moment from 'moment';
import { Button } from '@frontend/shared-ui-kit/button';
import { Link } from '@frontend/shared-ui-kit/link';
import { AppliedStatus } from './campaign-center-types';
import { useFlags } from 'launchdarkly-react-client-sdk';

const statusMap: any = {
  Approved: ['VERIFIED'],
  Pending: ['REJECTED', 'UNVERIFIED'],
  'Opted In': ['OPTED_IN'],
};

const FlatFeeTable = (props: any) => {
  const router = useRouter();
  const flags = useFlags();

  const state = useAppState();
  const { flatFeeDetails: data } = state.creatorCampaignCenter;

  const {
    selectedCampaign,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
  } = props;
  const publisherId = parseInt(router.query.publisherId as any);

  const columns = [
    {
      dataKey: 'month',
      label: 'Campaign Month',
      width: 1.5,
      sortable: false,
      keepMobileLabel: true,
      justifyContent: 'left',
      cellRender: ({ rowData }: any) => {
        const value = moment(rowData.month).utc().format('MMMM YYYY');
        return (
          <div className="flex">
            <div className="whitespace-normal">{value}</div>
          </div>
        );
      },
    },
    {
      dataKey: 'post_count',
      label: 'Content Required',
      width: 1.5,
      justifyContent: 'left',
      sortable: false,
    },
    {
      dataKey: 'payout',
      label: 'Flat Fee Payout',
      width: 1.5,
      justifyContent: 'left',
      sortable: false,
      cellRender: ({ rowData }: any) => {
        return <div className="">${parseInt(rowData.payout)}</div>;
      },
    },
    {
      dataKey: 'status',
      label: 'Status',
      width: 1,
      justifyContent: 'left',
      sortable: false,
      cellRender: ({ rowData }: any) => {
        const label = Object.keys(statusMap).find((label) =>
          statusMap[label].includes(rowData.status),
        );

        const formattedDate = moment(rowData.month).utc().format('YYYY-MM');
        const href = `/publisher/${router.query.publisherId}/payments/earnings?earningsBreakdownDate=${formattedDate}`;
        const content = <div className="capitalize">{label}</div>;
        return rowData.status === 'VERIFIED' ? (
          <Link
            href={href}
            className="flex items-center text-violet-300 text-sm focus:underline hover:underline"
          >
            {content}
          </Link>
        ) : (
          content
        );
      },
    },
  ];

  const columnItems = columns.map((column, index) => (
    <Column
      key={index}
      dataKey={column.dataKey}
      label={column.label}
      width={column.width}
      flexWidth={true}
      sortable={column.sortable}
      keepMobileLabel={column.keepMobileLabel}
      justifyContent={column.justifyContent}
      cellRender={column.cellRender}
    />
  ));

  const submitButton = (
    <Button
      width="w-44"
      size="lg"
      color="black"
      variant="outline"
      contentClassName="!px-0"
      rightIcon={<ExternalLink className="h-4 w-4 ml-2" />}
      onClick={() => {}}
      disabled={!data?.content_submission_url}
    >
      Submit Content
    </Button>
  );

  const invited = selectedCampaign.applied_status === AppliedStatus.Invited;

  return (
    <>
      <div className="px-4 sm:px-8 pt-4 sm:pt-8">
        <Link
          href={`/publisher/${publisherId}/campaign-center/brands/${selectedCampaign.merch_id}/`}
          className="flex items-center text-violet-300 text-sm focus:underline hover:underline"
        >
          <div
            data-cy="merch_name"
            className="text-sm font-medium tracking-wider mb-1 uppercase text-violet-400"
          >
            {selectedCampaign.merch_name}
          </div>
        </Link>
        <div data-cy="strategy_name" className="text-2xl font-bold">
          Details
        </div>
      </div>

      <div className="mx-4 sm:mx-8 text-deep-space-400 flex flex-wrap justify-center md:justify-start pt-4 sm:pt-5 mb-12">
        <div>
          {flags.flatFee3 && invited ? (
            <div className="text-center text-neutral-gray-600">
              Congrats! We would love to invite you to participate in this flat
              fee. We think you would be a great fit. Please check your email
              for our campaign agreement. Once signed, you can start creating
              content.
            </div>
          ) : (
            <>
              We’re so thrilled to have you participating! Use the button below
              to submit content for review. Once an entire month's content is
              reviewed, the status will display approved and link to your
              earnings.
            </>
          )}
        </div>
      </div>
      <div className="mx-0 sm:mx-8">
        {!(flags.flatFee3 && invited) && data ? (
          <Table
            data={data.flat_fees}
            stickyColumnsCount={1}
            selectedRows={{}}
            onSort={({ field, order }: any) => {
              setSortField(field);
              setSortDirection(order);
            }}
            sortField={sortField}
            sortOrder={sortDirection}
            sortIconClassName="mt-1"
            rowIdField="flat_fee_id"
            rowSize="sm"
          >
            {columnItems}
          </Table>
        ) : null}
      </div>
      {flags.flatFee3 && invited ? null : data?.content_submission_url ? (
        <>
          <div className="sm:block hidden border-b border-neutral-gray-300" />

          <a
            href={data.content_submission_url}
            target="_blank"
            rel="noopener noreferrer"
            data-ref="contact-howl"
            className="p-4 sm:p-8 block"
          >
            {submitButton}
          </a>
        </>
      ) : (
        <>
          <div className="sm:block hidden border-b border-neutral-gray-300" />
          <div className="p-4 sm:p-8">
            Submit content through the link provided in your Flat Fee agreement.
          </div>
        </>
      )}
    </>
  );
};
export default FlatFeeTable;
